// ----------------------------------------------------------------------------
// track-list — left panel track controls for DAWSEUR
//
// Each track item contains:
//   - Track number + editable name
//   - Color swatch
//   - Mute / Solo buttons
//   - Volume fader (faderH, -60 to +12 dB)
//   - Pan fader (faderH bipolar, -50 to +50)
//
// Uses the shared faderH component from ctrlz for slider controls.
// ----------------------------------------------------------------------------

const { faderH } = require('../../common/ctrlz.js');

// Track colors — grayscale from theme, no hardcoded RGB
const TRACK_COLORS = [
   '--cf', '--ce', '--cd', '--cc', '--cb', '--ca',
   '--c9', '--c8', '--c7', '--c6', '--c5', '--c4',
];

function createTrackList(opts = {}) {
   const container = document.getElementById('lw-track-list-items');
   const addBtn = document.getElementById('lw-add-track');
   if (!container) return null;

   const state = {
      tracks: [],           // [{ id, name, color, volume, pan, muted, solo, pitchShift }]
      selectedId: null,
      nextId: 1,
      onTrackSelected: opts.onTrackSelected || (() => {}),
      onTrackAdded: opts.onTrackAdded || (() => {}),
      onTrackRemoved: opts.onTrackRemoved || (() => {}),
      onVolumeChange: opts.onVolumeChange || (() => {}),
      onPanChange: opts.onPanChange || (() => {}),
      onMuteChange: opts.onMuteChange || (() => {}),
      onSoloChange: opts.onSoloChange || (() => {}),
      onFxToggle: opts.onFxToggle || (() => {}),
      onAutomationClick: opts.onAutomationClick || (() => {}),
      automationTracks: [], // [{ id, parentId, paramName, name, color }]
   };

   function addTrack(name, opts) {
      const id = (opts && opts.id !== undefined) ? opts.id : state.nextId++;
      // Ensure nextId stays ahead of any explicitly-provided id
      if (id >= state.nextId) state.nextId = id + 1;
      const colorVar = TRACK_COLORS[(id - 1) % TRACK_COLORS.length];
      const track = {
         id,
         name: name || `Track ${id}`,
         colorVar,  // CSS variable name (e.g. '--cf')
         color: '', // resolved color, set below
         volume: 0.8,  // linear gain 0-1
         pan: 0,       // -50 to +50
         muted: false,
         solo: false,
         fxEnabled: false,
         pitchShift: 0,
      };
      // Resolve CSS variable to actual color value
      const style = getComputedStyle(document.body);
      track.color = style.getPropertyValue(colorVar).trim() || '#a5a5a5';
      state.tracks.push(track);
      renderTrack(track);
      state.onTrackAdded(track);
      return track;
   }

   function removeTrack(id) {
      const idx = state.tracks.findIndex(t => t.id === id);
      if (idx >= 0) {
         state.tracks.splice(idx, 1);
         const el = container.querySelector(`[data-track-id="${id}"]`);
         if (el) el.remove();
         state.onTrackRemoved(id);
      }
   }

   function selectTrack(id) {
      state.selectedId = id;
      // Update visual selection
      container.querySelectorAll('.lw-track-item').forEach(el => {
         el.classList.toggle('selected', parseInt(el.dataset.trackId) === id);
      });
      const track = state.tracks.find(t => t.id === id);
      if (track) state.onTrackSelected(track);
   }

   function renderTrack(track) {
      const item = document.createElement('div');
      item.className = 'lw-track-item';
      item.dataset.trackId = track.id;

      // Header: number, name, color, mute, solo
      const header = document.createElement('div');
      header.className = 'lw-track-item-header';

      const num = document.createElement('span');
      num.className = 'lw-track-num';
      num.textContent = track.id;

      const name = document.createElement('input');
      name.className = 'lw-track-name';
      name.type = 'text';
      name.value = track.name;
      name.addEventListener('change', () => {
         track.name = name.value || `Track ${track.id}`;
      });
      name.addEventListener('focus', () => selectTrack(track.id));

      const buttons = document.createElement('div');
      buttons.className = 'lw-track-buttons';

      const fxBtn = document.createElement('button');
      fxBtn.className = 'lw-track-btn';
      fxBtn.textContent = 'FX';
      fxBtn.addEventListener('click', () => {
         track.fxEnabled = !track.fxEnabled;
         fxBtn.classList.toggle('active', track.fxEnabled);
         state.onFxToggle(track.id, track.fxEnabled);
      });

      const muteBtn = document.createElement('button');
      muteBtn.className = 'lw-track-btn';
      muteBtn.textContent = 'M';
      muteBtn.addEventListener('click', () => {
         track.muted = !track.muted;
         muteBtn.classList.toggle('active', track.muted);
         item.classList.toggle('muted', track.muted);
         state.onMuteChange(track.id, track.muted);
      });

      const soloBtn = document.createElement('button');
      soloBtn.className = 'lw-track-btn';
      soloBtn.textContent = 'S';
      soloBtn.addEventListener('click', () => {
         track.solo = !track.solo;
         soloBtn.classList.toggle('active', track.solo);
         state.onSoloChange(track.id, track.solo);
      });

      buttons.appendChild(fxBtn);
      buttons.appendChild(muteBtn);
      buttons.appendChild(soloBtn);

      header.appendChild(num);
      header.appendChild(name);
      header.appendChild(buttons);
      item.appendChild(header);

      // Faders: volume + pan (using faderH) + VU meter
      const faders = document.createElement('div');
      faders.className = 'lw-track-faders';

      // Inner column for vol + pan (stacked vertically)
      const faderCol = document.createElement('div');
      faderCol.className = 'lw-track-fader-col';

      // Volume fader (0 to 1 linear gain)
      const volHolder = document.createElement('div');
      volHolder.className = 'knobholder';
      const volInput = document.createElement('input');
      volInput.type = 'range';
      volInput.classList.add('ctrlinput');
      volInput.dataset.label = 'Vol';
      volInput.dataset.nocurve = '1';
      volInput.dataset.trackId = track.id;
      volInput.min = 0;
      volInput.max = 1;
      volInput.step = 0.01;
      volInput.value = 0.6;
      volInput.id = `_lw_vol_${track.id}`;
      volHolder.appendChild(volInput);
      faderCol.appendChild(volHolder);

      try {
         faderH(volInput);
      } catch (e) {
         console.warn('[dawseur] faderH init failed for vol:', e);
      }
      volInput.addEventListener('change', () => {
         track.volume = parseFloat(volInput.value);
         state.onVolumeChange(track.id, track.volume);
      });

      // Pan fader
      const panHolder = document.createElement('div');
      panHolder.className = 'knobholder';
      const panInput = document.createElement('input');
      panInput.type = 'range';
      panInput.classList.add('ctrlinput');
      panInput.dataset.label = 'Pan';
      panInput.dataset.nocurve = '1';
      panInput.dataset.trackId = track.id;
      panInput.min = -50;
      panInput.max = 50;
      panInput.step = 1;
      panInput.value = 0;
      panInput.id = `_lw_pan_${track.id}`;
      panHolder.appendChild(panInput);
      faderCol.appendChild(panHolder);

      try {
         faderH(panInput);
      } catch (e) {
         console.warn('[dawseur] faderH init failed for pan:', e);
      }
      panInput.addEventListener('change', () => {
         track.pan = parseFloat(panInput.value);
         state.onPanChange(track.id, track.pan);
      });

      // VU meter — vertical bar to the right of the faders
      const vuWrap = document.createElement('div');
      vuWrap.className = 'lw-track-vu-wrap';
      const vuBar = document.createElement('div');
      vuBar.className = 'lw-track-vu-bar';
      const vuFill = document.createElement('div');
      vuFill.className = 'lw-track-vu-fill';
      vuBar.appendChild(vuFill);
      vuWrap.appendChild(vuBar);

      faders.appendChild(faderCol);
      faders.appendChild(vuWrap);
      track._vuFill = vuFill;

      item.appendChild(faders);

      // Click to select
      item.addEventListener('click', (e) => {
         if (e.target === name || e.target.closest('.lw-track-btn') || e.target.closest('.ctrlwrapper')) return;
         selectTrack(track.id);
      });

      container.appendChild(item);
   }

   // ---- Add track button ----
   if (addBtn) {
      addBtn.addEventListener('click', () => addTrack());
   }

   // ---- Automation track rows ----
   function addAutomationTrack(parentTrackId, paramName, clip) {
      const id = 'auto_' + parentTrackId + '_' + paramName;
      // If already exists, update the clip reference
      const existing = state.automationTracks.find(a => a.id === id);
      if (existing) {
         existing.clip = clip;
         return existing;
      }
      const parentTrack = state.tracks.find(t => String(t.id) === String(parentTrackId));
      const autoTrack = {
         id: id,
         parentId: parentTrackId,
         paramName: paramName,
         name: (parentTrack ? parentTrack.name + ' › ' : '') + paramName,
         color: parentTrack ? parentTrack.color : '#a5a5a5',
         clip: clip,
      };
      state.automationTracks.push(autoTrack);
      renderAutomationTrack(autoTrack);
      return autoTrack;
   }

   function removeAutomationTrack(id) {
      const idx = state.automationTracks.findIndex(a => a.id === id);
      if (idx >= 0) {
         state.automationTracks.splice(idx, 1);
         const el = container.querySelector('[data-auto-id="' + CSS.escape(id) + '"]');
         if (el) el.remove();
      }
   }

   function renderAutomationTrack(autoTrack) {
      const item = document.createElement('div');
      item.className = 'lw-track-item lw-track-auto';
      item.dataset.autoId = autoTrack.id;

      const header = document.createElement('div');
      header.className = 'lw-track-item-header';

      const icon = document.createElement('span');
      icon.className = 'lw-track-num';
      icon.textContent = '\u2261'; // ≡ automation icon
      header.appendChild(icon);

      const name = document.createElement('span');
      name.className = 'lw-track-name lw-track-auto-name';
      name.textContent = autoTrack.name;
      header.appendChild(name);

      const delBtn = document.createElement('button');
      delBtn.className = 'lw-track-btn';
      delBtn.textContent = '\u2715';
      delBtn.title = 'Remove automation';
      delBtn.addEventListener('click', (e) => {
         e.stopPropagation();
         removeAutomationTrack(autoTrack.id);
         if (state.onAutomationClick) state.onAutomationClick('delete', autoTrack);
      });
      header.appendChild(delBtn);

      item.appendChild(header);

      item.addEventListener('click', () => {
         if (state.onAutomationClick) state.onAutomationClick('edit', autoTrack);
      });

      // Insert after the parent track (and after any existing auto tracks for the same parent)
      const parentEl = container.querySelector('[data-track-id="' + CSS.escape(String(autoTrack.parentId)) + '"]');
      if (parentEl) {
         // Find the last auto track for this parent that's right after the parent
         let insertAfter = parentEl;
         let next = parentEl.nextSibling;
         while (next) {
            if (next.classList && next.classList.contains('lw-track-auto') &&
                next.dataset.autoId && next.dataset.autoId.startsWith('auto_' + autoTrack.parentId + '_')) {
               insertAfter = next;
               next = next.nextSibling;
            } else {
               break;
            }
         }
         if (insertAfter.nextSibling) {
            container.insertBefore(item, insertAfter.nextSibling);
         } else {
            container.appendChild(item);
         }
      } else {
         container.appendChild(item);
      }
   }

   function getAutomationTracks() { return state.automationTracks; }

   // ---- Public API ----
   function getTracks() { return state.tracks; }
   function getTrack(id) { return state.tracks.find(t => t.id === id); }
   function getSelectedTrack() { return state.tracks.find(t => t.id === state.selectedId); }

   // Update VU meter for all tracks (called from render loop)
   function updateVUMeters(levels) {
      for (const track of state.tracks) {
         if (!track._vuFill) continue;
         const level = levels ? (levels[track.id] || 0) : 0;
         const pct = Math.min(100, level * 140);
         track._vuFill.style.height = pct + '%';
         track._vuFill.style.background = '';
      }
   }

   return {
      state,
      addTrack,
      removeTrack,
      selectTrack,
      getTracks,
      getTrack,
      getSelectedTrack,
      updateVUMeters,
      addAutomationTrack,
      removeAutomationTrack,
      getAutomationTracks,
   };
}

module.exports = { createTrackList };
